/**
 * Katalog der Einstellungen (AP-22 Teil 6, K1) — **welche** Schlüssel es gibt, wie sie
 * heißen und in welche Gruppe der Maske sie gehören.
 *
 * Bis Teil 6 stand diese Liste im Frontend (`features/payroll/lib/setting-keys.ts`); der
 * Server kannte sie nicht. Hier ist sie die eine Stelle für Maske, Server und die
 * KV-Vorlagen: Eine Vorlage muss in ihrem ersten Wertsatz jeden Schlüssel dieses Katalogs
 * tragen (ein Test hält das fest).
 *
 * Was ein Schlüssel **bewirkt**, steht in `parseKonfig` und in der Engine — der Katalog
 * beschreibt nur. Gespeichert wird jeder Wert als Text (`payroll_settings.value`).
 */

/** Wie der Wert in der Maske erscheint. */
export type EinstellungArt = 'zahl' | 'schalter' | 'text' | 'json'

/** Der Reiter der Einstellungsmaske. */
export type EinstellungGruppe = 'arbeitszeit' | 'ueberstunden' | 'zulagen' | 'pausen'

export interface EinstellungDef {
  key: string
  label: string
  art: EinstellungArt
  gruppe: EinstellungGruppe
  einheit?: string
}

export const EINSTELLUNGSKATALOG: readonly EinstellungDef[] = [
  { key: 'pause_aktiv', label: 'Automatischer Pausenabzug', art: 'schalter', gruppe: 'pausen' },
  { key: 'pause_dauer', label: 'Abzugsdauer', art: 'zahl', gruppe: 'pausen', einheit: 'min' },
  { key: 'pause_schwelle', label: 'Pause ab Arbeitszeit über', art: 'zahl', gruppe: 'pausen', einheit: 'h' },

  { key: 'ueberstunden_zeitraum', label: 'Abrechnungszeitraum', art: 'text', gruppe: 'ueberstunden' },
  { key: 'ueberstunden_zeitraum_monate', label: 'Länge der Periode', art: 'zahl', gruppe: 'ueberstunden', einheit: 'Monate' },
  { key: 'mehrarbeit_schwelle', label: 'Mehrarbeitsschwelle', art: 'zahl', gruppe: 'ueberstunden', einheit: 'h/Woche' },
  { key: 'ueberstunden_schwelle', label: 'Überstundenschwelle', art: 'zahl', gruppe: 'ueberstunden', einheit: 'h/Woche' },
  { key: 'mehrzeit_zuschlag_vollzeit', label: 'Mehrzeitzuschlag', art: 'zahl', gruppe: 'ueberstunden', einheit: '%' },
  { key: 'mehrzeit_zuschlag_teilzeit', label: 'Mehrzeitzuschlag Teilzeit', art: 'zahl', gruppe: 'ueberstunden', einheit: '%' },
  { key: 'umrechnung_zaehler', label: 'Umrechnung Zähler', art: 'zahl', gruppe: 'ueberstunden' },
  { key: 'umrechnung_nenner', label: 'Umrechnung Nenner', art: 'zahl', gruppe: 'ueberstunden' },

  { key: 'zuschlag_ueberstunde', label: 'Basissatz je Überstunde', art: 'zahl', gruppe: 'ueberstunden', einheit: '%' },
  { key: 'zuschlag_regeln', label: 'Zuschlagsregeln', art: 'json', gruppe: 'ueberstunden' },
  { key: 'sonntag_prozent', label: 'Sonntagszuschlag', art: 'zahl', gruppe: 'ueberstunden', einheit: '%' },
  { key: 'feiertag_prozent', label: 'Feiertagszuschlag', art: 'zahl', gruppe: 'ueberstunden', einheit: '%' },
  { key: 'zuschlag_ausserhalb_betriebszeit', label: 'Zuschlag außerhalb der Betriebszeit', art: 'zahl', gruppe: 'ueberstunden', einheit: '%' },
  { key: 'betriebszeiten', label: 'Betriebszeiten', art: 'json', gruppe: 'arbeitszeit' },
  { key: 'mehrarbeit_ueber_50_ist_ueberstunde', label: 'Mehrarbeit mit über 50 % Zuschlag gilt als Überstunde', art: 'schalter', gruppe: 'ueberstunden' },
  { key: 'ueberstunde_ab_zaehlt_mehrarbeit', label: 'Für „ab der x. Überstunde" zählt die Mehrarbeit des Tages mit', art: 'schalter', gruppe: 'ueberstunden' },
  { key: 'ueberstunde_100_arbeitsfrei_ab_stunde', label: 'Arbeitsfreier Tag: 100 % ab Arbeitsstunde', art: 'zahl', gruppe: 'ueberstunden' },
  { key: 'ueberstunde_100_ab_wochenstunde', label: '100 % ab Wochenstunde', art: 'zahl', gruppe: 'ueberstunden' },
  { key: 'rueckruf_prozent', label: 'Überstunden bei Rückruf', art: 'zahl', gruppe: 'ueberstunden', einheit: '%' },
  { key: 'halbtag_aktiv', label: '24. und 31. Dezember: Arbeitsende vorgezogen', art: 'schalter', gruppe: 'arbeitszeit' },
  { key: 'halbtag_ende', label: '24. und 31. Dezember: Arbeitsende', art: 'text', gruppe: 'arbeitszeit' },
  { key: 'halbtag_prozent', label: '24. und 31. Dezember: Zuschlag danach', art: 'zahl', gruppe: 'arbeitszeit', einheit: '%' },

  { key: 'montagezulage_satz', label: 'Satz je Montagestunde', art: 'zahl', gruppe: 'zulagen' },
  { key: 'entfernungszulage_klein', label: 'Entfernungszulage klein', art: 'zahl', gruppe: 'zulagen', einheit: 'je Tag' },
  { key: 'entfernungszulage_mittel', label: 'Entfernungszulage mittel', art: 'zahl', gruppe: 'zulagen', einheit: 'je Tag' },
  { key: 'entfernungszulage_gross', label: 'Entfernungszulage groß', art: 'zahl', gruppe: 'zulagen', einheit: 'je Tag' },
  { key: 'entfernungszulage_schwelle_klein', label: 'Schwelle kleine Zulage', art: 'zahl', gruppe: 'zulagen', einheit: 'h' },
  { key: 'entfernungszulage_schwelle_mittel', label: 'Schwelle mittlere Zulage', art: 'zahl', gruppe: 'zulagen', einheit: 'h' },
  { key: 'entfernungszulage_anreise_grenze', label: 'Anreisetag: Abfahrt vor', art: 'text', gruppe: 'zulagen' },
  { key: 'entfernungszulage_rueckreise_grenze', label: 'Rückreisetag: Ankunft nach', art: 'text', gruppe: 'zulagen' },
  { key: 'entfernungszulage_klein_mit_pause', label: 'Kleine Zulage: Pause zählt zur Abwesenheit', art: 'schalter', gruppe: 'zulagen' },
  { key: 'entfernungszulage_verpflegung_prozent', label: 'Große Zulage bei beigestellter Verpflegung', art: 'zahl', gruppe: 'zulagen', einheit: '%' },

  { key: 'rundung_aktiv', label: 'Zeit-Rundung', art: 'schalter', gruppe: 'arbeitszeit' },
  { key: 'rundung_schritt', label: 'Rundungsschritt', art: 'zahl', gruppe: 'arbeitszeit', einheit: 'min' },
]

const JE_KEY = new Map(EINSTELLUNGSKATALOG.map((d) => [d.key, d]))

const ZULAGE_ART: Record<string, EinstellungArt> = { aktiv: 'schalter', satz: 'zahl', von: 'text', bis: 'text' }

/**
 * Die Beschreibung eines Schlüssels. Die Schlüssel der Zulagen (`zulage_<zulage>_aktiv`,
 * `…_satz`, `…_von`, `…_bis`) stehen nicht in der festen Liste — sie gehören zum Katalog der
 * jeweiligen Vorlage (`einstellungskatalogFuer`); ihre Art steht im Namen. Alles andere
 * Unbekannte gibt es nicht.
 */
export function einstellungDef(key: string): EinstellungDef | undefined {
  const fest = JE_KEY.get(key)
  if (fest) return fest
  const m = /^zulage_[a-z0-9]+_(aktiv|satz|von|bis)$/.exec(key)
  return m ? { key, label: key, art: ZULAGE_ART[m[1]], gruppe: 'zulagen' } : undefined
}
